import { Box, Stack, Typography } from '@mui/material';
import { TbSteeringWheel } from 'react-icons/tb';
import type { MeasurementPreferences } from '@kangaroo/shared';
import type { ReactNode } from 'react';

import { useMeasurementPreferences } from '../../hooks/useMeasurementPreferences';
import { convertSpeedKph, formatMeasurement, speedUnitLabel } from '../../lib/measurements';
import { formatSpeedDeltaFromKph } from '../../lib/telemetryMeasurements';
import { formatTraceClock } from '../../lib/telemetrySession';

export type SessionCompareReplaySpeedReadout = {
  id: string;
  label: string;
  color: string;
  avatarUrl?: string | null;
  initials?: string | null;
  identityVariant?: number;
  speedKph: number | null;
  steeringDeg?: number | null;
  lateralG?: number | null;
  longitudinalG?: number | null;
};

type SessionCompareReplaySpeedHudProps = {
  readouts: SessionCompareReplaySpeedReadout[];
  elapsedSec: number;
  steeringDeg?: number | null;
  lateralG?: number | null;
  longitudinalG?: number | null;
  actionsSlot?: ReactNode;
};

function SteeringWheelIcon({ angleDeg }: { angleDeg: number | null | undefined }) {
  const visualAngle = Math.max(-180, Math.min(180, (angleDeg ?? 0) * 8));

  return (
    <Box aria-hidden="true" sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <TbSteeringWheel
        aria-hidden="true"
        size={36}
        strokeWidth={1.65}
        style={{
          color: '#FFFFFF',
          transform: `rotate(${visualAngle}deg)`,
          transformOrigin: 'center center',
          willChange: 'transform',
        }}
      />
    </Box>
  );
}

function GForceBullseye({
  dots,
}: {
  dots: Array<{ id: string; color: string; lateralG: number | null | undefined; longitudinalG: number | null | undefined }>;
}) {
  const plottedDots = dots.flatMap((dot) => {
    if (dot.lateralG === null || dot.lateralG === undefined || dot.longitudinalG === null || dot.longitudinalG === undefined) {
      return [];
    }

    const x = Math.max(-1.5, Math.min(1.5, dot.lateralG));
    const y = Math.max(-1.5, Math.min(1.5, dot.longitudinalG));

    return [{ ...dot, left: 50 + (x / 1.5) * 38, top: 50 - (y / 1.5) * 38 }];
  });

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        width: 58,
        height: 58,
        borderRadius: '50%',
        flexShrink: 0,
        background:
          'radial-gradient(circle, rgba(255,255,255,0.08) 0 2px, transparent 3px), radial-gradient(circle, transparent 0 28%, rgba(255,255,255,0.16) 29% 31%, transparent 32% 56%, rgba(255,255,255,0.12) 57% 59%, transparent 60%)',
        border: '1px solid rgba(36,62,82,0.72)',
        bgcolor: 'rgba(255,255,255,0.035)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', left: '50%', top: 7, bottom: 7, width: '1px', bgcolor: 'rgba(166,176,190,0.24)' }} />
      <Box sx={{ position: 'absolute', top: '50%', left: 7, right: 7, height: '1px', bgcolor: 'rgba(166,176,190,0.24)' }} />
      {plottedDots.map((dot, index) => (
        <Box
          key={dot.id}
          sx={{
            position: 'absolute',
            left: `${dot.left}%`,
            top: `${dot.top}%`,
            width: index === 0 ? 9 : 7,
            height: index === 0 ? 9 : 7,
            borderRadius: '50%',
            bgcolor: dot.color,
            border: '1px solid rgba(255,255,255,0.72)',
            boxShadow: `0 0 0 2px ${dot.color}33, 0 0 14px ${dot.color}88`,
            transform: 'translate(-50%, -50%)',
            zIndex: plottedDots.length - index,
          }}
        />
      ))}
    </Box>
  );
}

function formatLiveSpeedParts(speedKph: number | null, preferences: MeasurementPreferences) {
  if (speedKph === null || !Number.isFinite(speedKph)) {
    return { value: '—', unit: speedUnitLabel(preferences) };
  }

  return {
    value: formatMeasurement(convertSpeedKph(speedKph, preferences), 0),
    unit: speedUnitLabel(preferences),
  };
}

export function SessionCompareReplaySpeedHud({
  readouts,
  elapsedSec,
  steeringDeg,
  lateralG,
  longitudinalG,
  actionsSlot,
}: SessionCompareReplaySpeedHudProps) {
  const measurementPreferences = useMeasurementPreferences();
  const primary = readouts[0];
  const compares = readouts.slice(1);
  const primarySpeedKph = primary?.speedKph ?? null;
  const primarySpeed = formatLiveSpeedParts(primarySpeedKph, measurementPreferences);

  if (!primary) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        zIndex: 10,
        pointerEvents: 'none',
        maxWidth: 'min(100% - 24px, 360px)',
      }}
    >
      <Stack spacing={0.65}>
        <Stack
          spacing={0.75}
          sx={{
            px: 1.1,
            py: 0.9,
            borderRadius: '10px',
            border: '1px solid rgba(36,62,82,0.62)',
            bgcolor: 'rgba(1,13,24,0.86)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 26px rgba(0,0,0,0.32)',
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}>
            <Stack spacing={0.15} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.56rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.06em' }}>
                LIVE SPEED
              </Typography>
              <Stack direction="row" spacing={0.45} sx={{ alignItems: 'baseline' }}>
                <Typography
                  sx={{
                    fontSize: '2.35rem',
                    color: primary.color,
                    fontWeight: 950,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 0.95,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {primarySpeed.value}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#D8DEE8', fontWeight: 800, lineHeight: 1 }}>
                  {primarySpeed.unit}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.62rem', color: '#A6B0BE', fontWeight: 750, mt: 0.15 }}>
                {primary.label} | updates with replay
              </Typography>
            </Stack>

            <Stack spacing={0.1} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.56rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.04em' }}>
                LAP TIME
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.05rem',
                  color: '#FFFFFF',
                  fontWeight: 950,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}
              >
                {formatTraceClock(elapsedSec)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Box
          sx={{
            width: '100%',
            px: 1.15,
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: '10px',
            border: '1px solid rgba(36,62,82,0.62)',
            bgcolor: 'rgba(1,13,24,0.86)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 26px rgba(0,0,0,0.32)',
          }}
          title={steeringDeg === null || steeringDeg === undefined ? 'Steering unavailable' : `Steering ${steeringDeg.toFixed(0)} degrees`}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(36,62,82,0.72)',
              flexShrink: 0,
            }}
          >
            <SteeringWheelIcon angleDeg={steeringDeg} />
          </Box>
          <Stack
            direction="row"
            sx={{
              minWidth: 0,
              flex: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              '& > .hudMetric': { flex: '0 0 44px', alignItems: 'center' },
            }}
          >
            <Box className="hudMetric" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <Typography sx={{ fontSize: '1rem', color: '#FFFFFF', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                {steeringDeg === null || steeringDeg === undefined ? '—' : `${steeringDeg.toFixed(0)}°`}
              </Typography>
              <Typography sx={{ fontSize: '0.54rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.08em', mt: 0.35 }}>
                STEER
              </Typography>
            </Box>
            <Box sx={{ width: '1px', height: 42, bgcolor: 'rgba(36,62,82,0.72)', mx: 1.05, flexShrink: 0 }} />
            <Box className="hudMetric" sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <Typography sx={{ fontSize: '0.92rem', color: '#FFFFFF', fontWeight: 950, fontVariantNumeric: 'tabular-nums' }}>
                {lateralG === null || lateralG === undefined ? '—' : lateralG.toFixed(2)}
              </Typography>
              <Typography sx={{ fontSize: '0.54rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.08em', mt: 0.35 }}>
                LAT G
              </Typography>
            </Box>
            <Box sx={{ width: '1px', height: 42, bgcolor: 'rgba(36,62,82,0.72)', mx: 1.05, flexShrink: 0 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
              <GForceBullseye
                dots={readouts.map((readout) => ({
                  id: readout.id,
                  color: readout.color,
                  lateralG: readout.lateralG,
                  longitudinalG: readout.longitudinalG,
                }))}
              />
              <Typography sx={{ fontSize: '0.52rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.08em', lineHeight: 1 }}>
                LOAD
              </Typography>
            </Box>
          </Stack>
        </Box>

        {compares.length > 0 ? (
          <Stack spacing={0.55} sx={{ pt: 0.25 }}>
            {compares.map((readout) => {
              const deltaKph =
                primarySpeedKph !== null && readout.speedKph !== null
                  ? readout.speedKph - primarySpeedKph
                  : null;
              const speedParts = formatLiveSpeedParts(readout.speedKph, measurementPreferences);

              return (
                <Stack
                  key={readout.id}
                  spacing={0.55}
                  sx={{
                    width: '100%',
                    px: 1,
                    py: 0.65,
                    borderRadius: '10px',
                    border: '1px solid rgba(36,62,82,0.62)',
                    bgcolor: 'rgba(1,13,24,0.86)',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 8px 26px rgba(0,0,0,0.32)',
                  }}
                >
                  <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: 'rgba(2,10,18,0.82)',
                        backgroundImage: readout.avatarUrl ? `url("${readout.avatarUrl}")` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        border: `2px ${readout.identityVariant ? 'dashed' : 'solid'} ${readout.color}`,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#FFFFFF',
                        fontSize: '0.58rem',
                        fontWeight: 950,
                        lineHeight: 1,
                        boxShadow: '0 0 0 1px rgba(7,17,28,0.66)',
                      }}
                    >
                      {readout.avatarUrl ? null : readout.initials ?? readout.label.slice(0, 2).toUpperCase()}
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', color: '#FFFFFF', fontWeight: 900, lineHeight: 1, flexShrink: 0 }}>
                      {readout.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: '#FFFFFF', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1, flex: 1 }}>
                      {speedParts.value} {speedParts.unit}
                    </Typography>
                    {deltaKph !== null ? (
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: deltaKph >= 0 ? '#22C55E' : '#F87171', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                        {formatSpeedDeltaFromKph(deltaKph, measurementPreferences, 0)}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.7, pl: 3.65 }}>
                    {[
                      ['STEER', readout.steeringDeg == null ? '—' : `${Math.round(readout.steeringDeg)}°`],
                      ['LAT G', readout.lateralG == null ? '—' : readout.lateralG.toFixed(2)],
                      ['LONG G', readout.longitudinalG == null ? '—' : readout.longitudinalG.toFixed(2)],
                    ].map(([label, value]) => (
                      <Stack key={label} spacing={0.15}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#FFFFFF', fontWeight: 900, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                          {value}
                        </Typography>
                        <Typography sx={{ fontSize: '0.48rem', color: '#A6B0BE', fontWeight: 850, letterSpacing: '0.06em', lineHeight: 1 }}>
                          {label}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        ) : null}
        {actionsSlot}
      </Stack>
    </Box>
  );
}
